/**
 * Firebase Rules Tester
 * 
 * This file contains utility functions to test if Firebase security rules
 * are working correctly for new user signups.
 */

import { doc, setDoc } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';

/**
 * Interface for test results
 */
export interface TestResult {
  success: boolean;
  error?: unknown;
}

/**
 * Interface for combined test results
 */
export interface FirestorePermissionTestResults {
  companyTest: TestResult;
  userTest: TestResult;
}

/**
 * Tests company document creation permission
 * @param {string} uid - The user ID
 * @returns {Promise<TestResult>}
 */
export async function testCompanyCreationPermission(uid: string): Promise<TestResult> {
  try {
    console.log('Testing company creation permission for:', uid);
    const companyId = `company_${uid}`;
    
    // Attempt to create a test company document
    await setDoc(doc(db, 'companies', companyId), {
      id: companyId,
      name: 'Test Company',
      email: 'test@example.com',
      ownerId: uid,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    
    console.log('Company creation test successful');
    return { success: true };
  } catch (error) {
    console.error('Company creation test failed:', error);
    return { success: false, error };
  }
}

/**
 * Tests user document creation permission
 * @param {string} uid - The user ID
 * @returns {Promise<TestResult>}
 */
export async function testUserCreationPermission(uid: string): Promise<TestResult> {
  try {
    console.log('Testing user creation permission for:', uid);
    
    // Attempt to create a test user document
    await setDoc(doc(db, 'users', uid), {
      uid: uid,
      email: 'test@example.com',
      displayName: 'Test User',
      role: 'admin',
      companyId: `company_${uid}`,
      isActive: true,
      status: 'active',
      createdAt: new Date(),
      updatedAt: new Date(),
      lastLoginAt: new Date(),
    });
    
    console.log('User creation test successful');
    return { success: true };
  } catch (error) {
    console.error('User creation test failed:', error);
    return { success: false, error };
  }
}

/**
 * Run all Firestore permission tests
 * @returns {Promise<FirestorePermissionTestResults>}
 */
export async function runFirestorePermissionTests(): Promise<FirestorePermissionTestResults> {
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    console.error('No authenticated user found for testing');
    return {
      companyTest: { success: false, error: 'No authenticated user' },
      userTest: { success: false, error: 'No authenticated user' }
    };
  }
  
  const uid = currentUser.uid;
  const companyTest = await testCompanyCreationPermission(uid);
  const userTest = await testUserCreationPermission(uid);
  
  return {
    companyTest,
    userTest
  };
} 